// Sharing / remix data layer for the Game Studio (learn-game-studio-prd.md
// §17.7 J7 interactive class wall · §17.8 J8 external share-link · §17.10 J10
// remix · D-GAME8 / D-GAME10). Typed against the canonical backend contract.
//
// ROUTE-MOCKED in tests: every call goes through the shared `api` client (or, for
// the PUBLIC play route, a bare unauthed fetch) so the e2e suite can `page.route`
// each endpoint and run offline. No LLM is ever touched here — sharing publishes a
// frozen, read-only VFS snapshot; the public page is the bare game canvas only.
//
// Canonical endpoints (PRD §12.1 / §17.8 / §17.10):
//   POST   /projects/:id/share        mint a share-link directly
//   GET    /projects/:id/share        the current share-link state (pending/active)
//   DELETE /share/:shareId            revoke a share-link (→ later GETs serve 410)
//   GET    /play/:shareId/files       PUBLIC, no-auth frozen VFS (410 if revoked/expired)
//   GET    /classes/:id/wall/games    playable class-wall snapshots
//   POST   /classes/:id/wall/games/:postId/claps   add an anonymous clap (capped)
//   POST   /projects {remix_of}       fork a wall snapshot into a new owned project

import { ApiError, api } from '@/lib/api';
import type { VfsFile } from '../code/codeApi';
import type { GameEngine } from './buildGamePreview';
import {
  GAME_PROJECT_KIND,
  type ChatbotReplyResult,
  type SiteDbParam,
  type SiteDbQueryResult,
  type SiteSourceParam,
} from './panes/playgroundApi';
import { toStudioKind, type StudioKind } from './studioKind';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000';

// ── External share-link (J8 / D-GAME10) ────────────────────────────────────

/**
 * A share-link's lifecycle state. A student create freezes and safety-checks the
 * snapshot, then returns the capability URL (`active`) immediately. `pending`
 * remains only for unclaimed walk-in students who need teacher review. Revoke/expiry → the public route
 * serves 410 (surfaced to a visitor as `ShareGoneError`).
 */
export type ShareStatus = 'none' | 'pending' | 'active' | 'revoked';

export interface ShareLink {
  status: ShareStatus;
  /** The link id — plays (`/play/:shareId`) once `active`; revokes/cancels in any state. */
  shareId?: string;
  /** ISO timestamp the link auto-expires (→ 410). */
  expires_at?: string | null;
  /** Whether an anonymized display handle is shown to visitors (OD-7), vs none. */
  show_handle?: boolean;
  /** Engagement (active links): how many times the game was played (= opened). */
  plays?: number;
  /**
   * Whether visitors may fork this link into their own account (remix-from-link).
   * Default OFF; the author sets it and the parent can change it afterwards.
   */
  remix_allowed?: boolean;
  /** The shared project's kind (chatbot shares carry a visitor budget, D-CB-09). */
  kind?: StudioKind;
  /** Chatbot shares (creative-code-studio-chatbot-prd D-CB-09): the author-set
   *  LIFETIME total (Stars) visitors may spend; null = visitors cannot chat. */
  visitor_star_budget?: number | null;
  /** Chatbot shares: how much of that budget visitors have spent so far. */
  visitor_stars_spent?: number;
  /** Chatbot shares: visitor Stars still available (null for other kinds). */
  visitor_budget_remaining?: number | null;
}

/** Backend ShareView (snake_case) → FE ShareLink. */
interface RawShareView {
  status: ShareStatus;
  share_id?: string;
  expires_at?: string | null;
  show_handle?: boolean;
  plays?: number;
  remix_allowed?: boolean;
  kind?: string;
  visitor_star_budget?: number | null;
  visitor_stars_spent?: number;
  visitor_budget_remaining?: number | null;
}
const toShareLink = (r: RawShareView): ShareLink => ({
  status: r.status,
  shareId: r.share_id,
  expires_at: r.expires_at ?? null,
  show_handle: r.show_handle ?? false,
  plays: r.plays ?? 0,
  remix_allowed: r.remix_allowed ?? false,
  kind: toStudioKind(r.kind),
  visitor_star_budget: r.visitor_star_budget ?? null,
  visitor_stars_spent: r.visitor_stars_spent ?? 0,
  visitor_budget_remaining: r.visitor_budget_remaining ?? null,
});

// ── Try-demo seam (try-demo-mode-prd D-DEMO-09) ─────────────────────────────
// The /try/* demos WALK the real share flow with ZERO network. An in-memory
// share-lifecycle adapter (none → active) intercepts the share calls below when installed; the real
// ShareLinkPanel / BlocksSharePanel render unchanged off its states. The public
// play page resolves a build-time BUNDLED snapshot for the two fixed demo
// shareIds — so a REAL new tab to /play/:shareId renders the unmodified
// PublicPlayPage offline (a fresh tab has no demo install, hence a bundled
// constant, not session state — same class as demoStarter/demoStory; no demo
// project/session state is ever persisted, D-DEMO-02 intact).

/** The fixed share ids whose /play snapshot is a bundled constant (D-DEMO-09). */
export const DEMO_PLAYGROUND_SHARE_ID = 'try-demo-playground';
export const DEMO_BLOCKS_SHARE_ID = 'try-demo-blocks';

/** A single-slot in-memory share lifecycle for one demo surface. */
export interface DemoShareAdapter {
  get(): ShareLink;
  request(): ShareLink; // → active
  approve(): ShareLink; // compatibility for older demo callers
  revoke(): ShareLink; // → none
}
let demoShareAdapter: DemoShareAdapter | null = null;
export function setDemoShareAdapter(adapter: DemoShareAdapter | null): void {
  demoShareAdapter = adapter;
}

/**
 * A frozen, read-only game snapshot for the public play page / class wall. Carries
 * the {@link GameEngine} it was published under (learn-game-studio-3d-prd.md
 * D-3D-01) so the bare-canvas frame loads the RIGHT engine — Phaser (2D) vs
 * three.js (3D). Without it a 3D game silently renders nothing (defaults to Phaser).
 */
export interface PublicSnapshot {
  files: VfsFile[];
  engine: GameEngine;
  /**
   * What the frozen snapshot IS — the public host renders the matching
   * read-only frame (D-WEB-22): `website` → the read-only `SiteFrame`;
   * `game` → the bare game/blocks canvas. Captured on the ShareLink at approval
   * (from `Project.kind`) so the public host classifies with NO project row. A
   * pre-kind backend / bundled demo snapshot defaults to `game`.
   */
  kind: StudioKind;
}

/** Bundled, offline `/play/:shareId` snapshots for the two demos (D-DEMO-09). */
const DEMO_SNAPSHOTS: Record<string, () => Promise<VfsFile[]>> = {
  [DEMO_PLAYGROUND_SHARE_ID]: () =>
    import('../../try/demoSnapshot.playground').then((m) => m.demoGameSnapshot()),
  [DEMO_BLOCKS_SHARE_ID]: () =>
    import('../../try/demoSnapshot.blocks').then((m) => m.demoBlocksSnapshot()),
};

/**
 * Create a share-link directly (J8). Returns `active` after the safety scan.
 * An unclaimed walk-in student returns `pending` for teacher review because no
 * parent exists. Teacher-prep projects also mint on this call.
 */
export async function requestShareLink(
  projectId: string,
  opts: { remixAllowed?: boolean; visitorStarBudget?: number } = {},
): Promise<ShareLink> {
  if (demoShareAdapter) return demoShareAdapter.request();
  return toShareLink(
    await api<RawShareView>(`/projects/${projectId}/share`, {
      method: 'POST',
      // The author's remix setting (default OFF) and initial chatbot visitor
      // budget. The parent receives the active link and can change either later.
      body: {
        remix_allowed: opts.remixAllowed ?? false,
        ...(opts.visitorStarBudget !== undefined
          ? { visitor_star_budget: opts.visitorStarBudget }
          : {}),
      },
    }),
  );
}

/**
 * A PARENT raises / lowers an ACTIVE chatbot share's visitor budget
 * (`PATCH /projects/:id/share/budget`, creative-code-studio-chatbot-prd
 * D-CB-09). 0 = pause visitors (nothing more may be spent); the link keeps
 * showing the page. Parent-only server-side.
 */
export async function setShareVisitorBudget(args: {
  projectId: string;
  visitorStarBudget: number;
}): Promise<ShareLink> {
  return toShareLink(
    await api<RawShareView>(`/projects/${args.projectId}/share/budget`, {
      method: 'PATCH',
      body: { visitor_star_budget: args.visitorStarBudget },
    }),
  );
}

/** The current share-link state for a project (none/pending/active). */
export async function getShareLink(projectId: string): Promise<ShareLink> {
  if (demoShareAdapter) return demoShareAdapter.get();
  try {
    return toShareLink(await api<RawShareView>(`/projects/${projectId}/share`));
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return { status: 'none' };
    throw e;
  }
}

/**
 * Toggle the anonymized display handle vs no author label on the public page
 * (OD-7 / §17.8). Never exposes kid PII — it's a generated handle, not a real name.
 */
export async function setShareHandle(args: {
  projectId: string;
  showHandle: boolean;
}): Promise<ShareLink> {
  return toShareLink(
    await api<RawShareView>(`/projects/${args.projectId}/share`, {
      method: 'PUT',
      body: { show_handle: args.showHandle },
    }),
  );
}

/**
 * Flip an ACTIVE link's remix consent without touching the link itself
 * (remix-from-link, parent-only server-side): `PUT /projects/:id/share
 * { remix_allowed }` — both directions allowed; existing forks are untouched
 * (owner decision 2). Distinct from revoke, which kills playing AND remixing.
 */
export async function setShareRemixAllowed(args: {
  projectId: string;
  remixAllowed: boolean;
}): Promise<ShareLink> {
  return toShareLink(
    await api<RawShareView>(`/projects/${args.projectId}/share`, {
      method: 'PUT',
      body: { remix_allowed: args.remixAllowed },
    }),
  );
}

/** Revoke OR cancel a share-link (kid OR parent). Subsequent public GETs serve 410. */
export async function revokeShareLink(shareId: string): Promise<void> {
  if (demoShareAdapter) {
    demoShareAdapter.revoke();
    return;
  }
  await api<void>(`/share/${shareId}`, { method: 'DELETE' });
}

// ── Parent approval + management (Portal, J8 / D-GAME10a) ───────────────────

/** A family share-link as the parent sees it: `pending` (decide) or `active` (revoke + metrics). */
export interface FamilyShareLink {
  share_id: string;
  status: 'pending' | 'active';
  project_id: string;
  project_title: string;
  kid_id: string | null;
  requested_at: string;
  plays: number;
  /** The link's remix consent: the kid's requested value while `pending`, the
   *  live value once `active`. Absent on a pre-remix backend → treat as false. */
  remix_allowed?: boolean;
  /** The project kind — a `chatbot` link shows the visitor-budget controls. */
  kind?: string;
  /** Chatbot links (D-CB-09): requested (pending) / approved (active) budget. */
  visitor_star_budget?: number | null;
  visitor_stars_spent?: number;
  visitor_budget_remaining?: number | null;
}

/** The family's share-links (pending awaiting approval + active, revocable). */
export async function listFamilyShareLinks(familyId: string): Promise<FamilyShareLink[]> {
  try {
    return await api<FamilyShareLink[]>(`/families/${familyId}/share-requests`);
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return [];
    throw e;
  }
}

/** Parent approves a request → mints the link (freezes a PII-stripped snapshot).
 *  In the demo this is the ONE simulated beat (no real parent) — the tour fires
 *  it behind preview-framed copy (D-DEMO-09). */
export async function approveShareLink(
  projectId: string,
  opts: { remixAllowed?: boolean; visitorStarBudget?: number; showNickname?: boolean } = {},
): Promise<ShareLink> {
  if (demoShareAdapter) return demoShareAdapter.approve();
  return toShareLink(
    await api<RawShareView>(`/projects/${projectId}/share/approve`, {
      method: 'POST',
      // The parent's FINAL say on remix consent (and, for a chatbot, on the
      // visitor Star budget — D-CB-09). Omitted → the kid's requested value
      // stands (backend contract); a parent can approve play but veto remix.
      body: {
        ...(opts.remixAllowed === undefined ? {} : { remix_allowed: opts.remixAllowed }),
        ...(opts.visitorStarBudget === undefined
          ? {}
          : { visitor_star_budget: opts.visitorStarBudget }),
        // Share page nickname (parent-growth-rewards-prd.md §5 F3.2): a parent
        // opt-in, off unless the parent ticks it. Only a parent's true counts.
        ...(opts.showNickname === undefined ? {} : { show_nickname: opts.showNickname }),
      },
    }),
  );
}

/** The public play page got a 410 — the link was revoked or expired (J8). */
export class ShareGoneError extends Error {
  constructor() {
    super('share_gone');
    this.name = 'ShareGoneError';
  }
}

/**
 * Public metadata for a share link (`GET /play/:shareId/meta` — no auth, same
 * 410 semantics as the files route). `remix_allowed` is the EFFECTIVE value
 * (link consent AND the global kill-switch AND kind support) — the play page
 * shows the remix CTA only when it is true, and shows NOTHING remix-related
 * otherwise.
 */
export interface PlayMeta {
  title: string;
  /** Anonymized author display handle (never a real name), or null. */
  display_handle: string | null;
  claps: number;
  plays: number;
  remix_allowed: boolean;
  /**
   * The shared project's kind — the REAL union (blocks share links exist), not
   * just the play-surface split: a remix fork of a `blocks` link opens in the
   * Blocks Studio (`/learn/blocks/:id`), everything else in the playground.
   * Unknown/absent kinds normalise to `game` (the pre-kind default).
   */
  kind: StudioKind | 'blocks';
  engine: GameEngine;
  /** Chatbot shares (D-CB-09): visitor Stars still available; null otherwise
   *  (and for a paused / budget-less bot the meter reads 0). */
  visitor_budget_remaining: number | null;
  visitor_star_budget: number | null;
  visitor_stars_spent: number;
}

/**
 * Fetch the public play-page metadata. Bare `fetch` (NOT the `api` client) so no
 * auth token is ever sent on the public route (D-GAME10d). 410 → ShareGoneError.
 */
export async function getPlayMeta(shareId: string): Promise<PlayMeta> {
  let res: Response;
  try {
    res = await fetch(`${BASE_URL}/play/${shareId}/meta`, { method: 'GET' });
  } catch {
    throw new Error('network');
  }
  if (res.status === 410) throw new ShareGoneError();
  if (!res.ok) throw new Error(`play_meta_${res.status}`);
  const body = (await res.json()) as Partial<PlayMeta>;
  return {
    title: body.title ?? '',
    display_handle: body.display_handle ?? null,
    claps: body.claps ?? 0,
    plays: body.plays ?? 0,
    remix_allowed: body.remix_allowed === true,
    kind: body.kind === 'blocks' ? 'blocks' : toStudioKind(body.kind),
    engine: body.engine === 'three' ? 'three' : 'phaser',
    visitor_budget_remaining:
      typeof body.visitor_budget_remaining === 'number' ? body.visitor_budget_remaining : null,
    visitor_star_budget:
      typeof body.visitor_star_budget === 'number' ? body.visitor_star_budget : null,
    visitor_stars_spent:
      typeof body.visitor_stars_spent === 'number' ? body.visitor_stars_spent : 0,
  };
}

/**
 * Load a frozen, read-only VFS snapshot for the PUBLIC play page. Uses a BARE
 * `fetch` (NOT the `api` client) so NO auth token is sent — the public route
 * carries no auth and no `/llm/*` access (D-GAME10d). A revoked/expired link
 * returns 410 → `ShareGoneError` so the host can show the gone state.
 */
export async function readPublicSnapshot(shareId: string): Promise<PublicSnapshot> {
  // Demo shareIds (D-DEMO-09): a build-time bundled snapshot, ZERO network — so a
  // real new tab to /play/try-demo-* renders this real page offline. Resolved
  // unconditionally (no demo install needed in a fresh tab); the ids never
  // collide with real capability tokens. Both demos are 2D games (phaser).
  const demoSnapshot = DEMO_SNAPSHOTS[shareId];
  if (demoSnapshot) return { files: await demoSnapshot(), engine: 'phaser', kind: 'game' };

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}/play/${shareId}/files`, { method: 'GET' });
  } catch {
    throw new Error('network');
  }
  if (res.status === 410) throw new ShareGoneError();
  if (!res.ok) throw new Error(`play_${res.status}`);
  const body = (await res.json()) as { files?: VfsFile[]; engine?: GameEngine; kind?: string };
  // A pre-fix backend (or a snapshot with no engine) means the 2D Phaser default;
  // an absent kind means a game (websites are the newer, explicit kind, D-WEB-22).
  return {
    files: body.files ?? [],
    engine: body.engine === 'three' ? 'three' : 'phaser',
    kind: toStudioKind(body.kind),
  };
}

// ── Public per-share website backend (D-WEB-22) ─────────────────────────────
// A `/play/:shareId` visitor has NO project and NO auth, yet the frozen site's
// `server.js` still calls `db.query` / `sources.get`. The read-only host proxies
// those to NO-AUTH endpoints keyed by shareId (bare `fetch`, never the `api`
// client — no `Authorization` header is ever sent). The db endpoint hands back
// an opaque per-visitor `session` (an isolated ephemeral clone of the frozen
// db); the client persists it per visit and echoes it on later calls, so a
// visitor's writes are THEIRS (reset on reload) and never touch the author's db.

/** Parse a `/play/:shareId` error response into an {@link ApiError} carrying the
 *  backend's kid-readable message (same envelope as the `api` client) so the
 *  site frame surfaces it verbatim, exactly like the studio proxy does. */
async function publicPlayError(res: Response): Promise<ApiError> {
  let code = `HTTP_${res.status}`;
  let message = res.statusText;
  try {
    const body = (await res.json()) as { error?: { code: string; message: string } };
    if (body.error) {
      code = body.error.code;
      message = body.error.message;
    }
  } catch {
    // body wasn't JSON — keep the status-based defaults
  }
  return new ApiError(res.status, code, message);
}

/** A public site db.query result plus the opaque per-visitor session token the
 *  caller must persist (per tab/visit) and echo on subsequent calls. */
export interface PublicSiteDbResult {
  result: SiteDbQueryResult;
  session: string;
}

/**
 * Run ONE SQL statement against a shared site's ephemeral per-visitor db clone
 * (D-WEB-22). NO auth. The FIRST call omits `session` and the backend mints one
 * (returned alongside the query envelope); every later call echoes it so the
 * visitor keeps the same isolated clone. Backend errors arrive as an
 * {@link ApiError} whose message is kid-readable — callers show it verbatim.
 */
export async function queryPublicSiteDb(
  shareId: string,
  sql: string,
  params: SiteDbParam[] = [],
  session?: string,
): Promise<PublicSiteDbResult> {
  const res = await fetch(`${BASE_URL}/play/${shareId}/db/query`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ sql, params, ...(session ? { session } : {}) }),
  });
  if (!res.ok) throw await publicPlayError(res);
  const body = (await res.json()) as SiteDbQueryResult & { session: string };
  const { session: nextSession, ...result } = body;
  return { result, session: nextSession };
}

/**
 * Fetch one curated data source for a shared site through the public per-share
 * proxy (D-WEB-22). NO auth; hits the SAME curated catalog as the studio
 * (read-only, cached, no kid data). Errors arrive as a kid-readable
 * {@link ApiError}, surfaced verbatim.
 */
export async function fetchPublicSource(
  shareId: string,
  name: string,
  params: Record<string, SiteSourceParam>,
): Promise<{ data: unknown; cached: boolean }> {
  const res = await fetch(`${BASE_URL}/play/${shareId}/sources/${encodeURIComponent(name)}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ params }),
  });
  if (!res.ok) throw await publicPlayError(res);
  return (await res.json()) as { data: unknown; cached: boolean };
}

/**
 * The OPEN fetch door for a shared site (D-WEB-23): any public https JSON API
 * through the NO-AUTH per-share proxy (`POST /play/:shareId/sources/fetch` —
 * stateless, no session; the server's SSRF fence + admin domain blocklist
 * apply exactly as in the studio). Errors arrive as a kid-readable
 * {@link ApiError} (`SOURCE_URL` / `SOURCE_BLOCKED` / `SOURCE_UPSTREAM` /
 * `SOURCE_BUSY`), surfaced verbatim.
 */
export async function fetchPublicSourceUrl(
  shareId: string,
  url: string,
): Promise<{ data: unknown; cached: boolean }> {
  const res = await fetch(`${BASE_URL}/play/${shareId}/sources/fetch`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  if (!res.ok) throw await publicPlayError(res);
  return (await res.json()) as { data: unknown; cached: boolean };
}

/**
 * ONE runtime reply from a SHARED chatbot for a no-auth `/play` visitor
 * (`POST /play/:shareId/chatbot/reply`, creative-code-studio-chatbot-prd
 * D-CB-09). Bare `fetch` — no token ever rides the public route. The FIRST
 * call omits `session` and the backend mints the opaque per-visitor session
 * (returned with the reply); later calls echo it for the per-session cap. The
 * AUTHOR's wallet pays, inside the parent-approved visitor budget; a
 * `BUDGET_EXHAUSTED` / `PAUSED` / `CHATBOT_PUBLIC_DISABLED` failure arrives as
 * an {@link ApiError} the frame maps to the page's typed codes.
 */
export async function sendPublicChatbotReply(
  shareId: string,
  args: {
    text: string;
    history: Array<{ role: 'user' | 'bot'; text: string }>;
    idempotencyKey?: string;
  },
  session?: string,
): Promise<ChatbotReplyResult> {
  const res = await fetch(`${BASE_URL}/play/${shareId}/chatbot/reply`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      text: args.text,
      history: args.history,
      idempotency_key: args.idempotencyKey ?? crypto.randomUUID(),
      ...(session ? { session } : {}),
    }),
  });
  if (!res.ok) throw await publicPlayError(res);
  return (await res.json()) as ChatbotReplyResult;
}

// ── Interactive class wall (J7 / D-GAME8) ───────────────────────────────────

/**
 * A playable class-wall game: a published, read-only VFS snapshot rendered in the
 * opaque-origin sandbox (D-GAME8 — the wall never exposes an editable surface).
 * `claps` is the capped anonymous reaction count (OD-8): a number with no
 * identities, no messages, no comments.
 */
export interface WallGame {
  /** WallPost id (the clap/remix subject — NOT the project id). */
  postId: string;
  shareId: string;
  title: string;
  /** Anonymized author display handle (no PII), or null for anonymous. */
  handle: string | null;
  /** Capped anonymous clap count (OD-8 equity guard — shown capped, not ranked). */
  claps: number;
  /** Whether this kid already clapped (one clap per kid per post). */
  clapped: boolean;
  /** Whether the teacher enabled remix for this class (J10). */
  remix_enabled: boolean;
  /** Attribution shown when this game is itself a remix ("Remixed from …"). */
  remixed_from?: string | null;
}

/** The cap on the visible clap count (OD-8 equity guard — show "lots" past it). */
export const CLAP_CAP = 50;

/** Playable games shared to a class wall (J7). Degrades to empty offline. */
export async function getWallGames(classId: string): Promise<WallGame[]> {
  try {
    const res = await api<WallGame[] | { games: WallGame[] }>(`/classes/${classId}/wall/games`);
    return Array.isArray(res) ? res : (res.games ?? []);
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return [];
    throw e;
  }
}

/**
 * Add one anonymous clap to a wall game (OD-8). Capped server-side AND surfaced
 * capped client-side — one clap per kid per post; no identity is attached.
 */
export async function clapWallGame(args: { classId: string; postId: string }): Promise<void> {
  await api<void>(`/classes/${args.classId}/wall/games/${args.postId}/claps`, { method: 'POST' });
}

// ── Remix (J10) ─────────────────────────────────────────────────────────────

/**
 * Fork a class-wall game's frozen snapshot into a NEW project owned by the
 * remixer (J10). The backend checks class-wall membership + remix-allowed, copies
 * the snapshot into a new owned VFS prefix, and records `remix_of` + attribution;
 * the original is never modified. Returns the new project id to open the studio.
 */
export async function remixWallGame(args: {
  shareId: string;
  kidId: string | null;
  familyId: string | null;
}): Promise<{ id: string }> {
  return api<{ id: string }>(`/projects`, {
    method: 'POST',
    body: {
      product_line: 'line_b_coding',
      kind: GAME_PROJECT_KIND,
      remix_of: args.shareId,
      ...(args.kidId ? { kid_id: args.kidId } : {}),
      ...(args.familyId ? { family_id: args.familyId } : {}),
    },
  });
}

/**
 * Fork a PUBLIC share link's frozen snapshot into a new private project
 * (remix-from-link). The backend authorizes on the link's `remix_allowed` (plus
 * the global kill-switch), copies the snapshot, and records attribution — the
 * original is never touched. 403 `REMIX_NOT_ALLOWED` when remixing is off;
 * 410 when the link is gone.
 *
 * A signed-in KID forks for themself (`principal: 'kid'`, no `kid_id`). A
 * signed-in PARENT forks on behalf of one of their kids (`principal: 'user'` +
 * `kid_id`, family-scoped server-side) — the copy lands in that kid's My Works.
 */
export async function remixFromShareLink(args: {
  shareId: string;
  principal: 'kid' | 'user';
  kidId?: string;
}): Promise<{ id: string }> {
  return api<{ id: string }>(`/projects`, {
    method: 'POST',
    principal: args.principal,
    body: {
      remix_of: args.shareId,
      ...(args.kidId ? { kid_id: args.kidId } : {}),
    },
  });
}
